/**
 * useKnockMode — the RENDER seam for Knock Mode (KNK-1.11, FR-11).
 *
 * Wires `knock_mode_status()` for the ACTIVE account to the pure decisions in
 * `@/lib/knockMode`. It is not the gate; see that file's header.
 *
 * ⚠️ While `loading` is true, render nothing for Knock Mode. A read error leaves the status at
 * OFF, which hides the feature: FR-11's safe state, not a guess that it is on.
 *
 * Re-reads on an account switch, keyed on TenantContext's active tenant rather than
 * `userInfo.tenant_id`, which is a superadmin's HOME account (the W1R P13 lesson in
 * useCommissionCostingEnabled).
 */
import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { KNOCK_MODE_OFF, type KnockModeStatus } from '@/lib/knockMode';
import { fetchKnockModeStatus } from '@/services/knockModeService';

// The sidebar and the Settings panel both mount this hook, and StrictMode mounts each twice:
// one read per account in flight, shared, instead of four identical RPCs.
const inflight = new Map<string, Promise<KnockModeStatus>>();
function readShared(tenantId: string): Promise<KnockModeStatus> {
  let p = inflight.get(tenantId);
  if (!p) {
    p = fetchKnockModeStatus().finally(() => inflight.delete(tenantId));
    inflight.set(tenantId, p);
  }
  return p;
}

// KNK-1.55. Knock Mode is switched on another screen (Settings → Add-on modules), and the sidebar
// and the Knock Mode panel each hold their own copy of the status. This tells every mounted copy to
// re-read, so the entry appears or disappears without a page reload.
const KNOCK_MODE_CHANGED = 'joules:knock-mode-changed';
export function notifyKnockModeChanged(): void {
  window.dispatchEvent(new Event(KNOCK_MODE_CHANGED));
}

export interface KnockModeState {
  status: KnockModeStatus;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Adopt a status a write already returned, without a second read. */
  setStatus: (s: KnockModeStatus) => void;
}

export function useKnockMode(): KnockModeState {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id ?? null;
  const [status, setStatus] = useState<KnockModeStatus>(KNOCK_MODE_OFF);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      setStatus(await readShared(tenantId));
    } catch (e) {
      setStatus(KNOCK_MODE_OFF);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onChanged = () => void reload();
    window.addEventListener(KNOCK_MODE_CHANGED, onChanged);
    return () => window.removeEventListener(KNOCK_MODE_CHANGED, onChanged);
  }, [reload]);

  return { status, loading: loading || !tenantId, error, reload, setStatus };
}
